import React, { CSSProperties, ReactNode } from 'react';
import { PermissionKey } from '../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../services/PermissionService';
import { GenericFallbacksWrapper } from './GenericFallbacksWrapper';
import { ForbiddenError, IForbiddenErrorConfig } from './GenericErrorFallback';

export interface IPermissionGatedWrapperProps {
  /**
   * Single permission or array of permissions required to access the content.
   * Example: PermissionKey.canViewCompanyBasics
   * Example: [PermissionKey.canViewCompanyBasics, PermissionKey.canViewMetrics]
   */
  permissions: PermissionKey[];

  /**
   * When multiple permissions provided, determines if ALL (AND) or ANY (OR) must be satisfied.
   * Default: true (requires all permissions)
   */
  requireAll?: boolean;

  /**
   * Fallback message configuration to show when access is denied.
   * Use PRIMARY/SECONDARY/TERTIARY constants from forbidden-messages.constants.ts
   */
  fallbackMessage: IForbiddenErrorConfig;

  /**
   * Content to render when user has required permissions
   */
  children: ReactNode;

  /**
   * Optional styles for the wrapper container
   */
  style?: CSSProperties;
}

/**
 * PermissionGatedWrapper - Proactive permission checking wrapper
 *
 * Checks user permissions BEFORE rendering children, preventing unnecessary
 * API calls and showing appropriate forbidden messages immediately.
 *
 * Also wraps children with GenericFallbacksWrapper to handle other errors.
 *
 * @example Single permission
 * <PermissionGatedWrapper
 *   permissions={PermissionKey.canViewCompanyBasics}
 *   fallbackMessage={PrimaryForbiddenMessage}
 * >
 *   <MyPanel />
 * </PermissionGatedWrapper>
 *
 * @example Multiple permissions (requires both)
 * <PermissionGatedWrapper
 *   permissions={[PermissionKey.canViewCompanyBasics, PermissionKey.canViewMetrics]}
 *   requireAll={true}
 *   fallbackMessage={PrimaryForbiddenMessage}
 * >
 *   <MyPanel />
 * </PermissionGatedWrapper>
 */
export function PermissionGatedWrapper(props: IPermissionGatedWrapperProps) {
  const { permissions, requireAll = true, fallbackMessage, children, style } = props;

  const permSvc = PermissionService.get();

  // Check permissions proactively
  const hasAccess = requireAll
    ? permissions.every((p) => permSvc.hasPermission(p))
    : permissions.some((p) => permSvc.hasPermission(p));

  // Show forbidden fallback immediately if no access
  if (!hasAccess) {
    return (
      <ForbiddenError error={new Error('Permission denied')} config={fallbackMessage} style={style ?? {}} />
    );
  }

  // User has access - still wrap with error boundary for other errors
  return (
    <GenericFallbacksWrapper forbiddenFallback={fallbackMessage} style={style}>
      {children}
    </GenericFallbacksWrapper>
  );
}
